import { TFunction } from "i18next";
import * as yup from "yup";

import { AndragogicLeaderDeleteState } from "./AndragogicLeader";
import { LocationDeleteState } from "./InstitutionLocation";

export interface InstitutionValidationSchemeProps {
  t: TFunction;
}

export const InstitutionValidationScheme = ({
  t,
}: InstitutionValidationSchemeProps) => {
  const requiredMsg = t("validations.required");
  const invalidEmailMsg = t("validations.invalidEmail");
  const invalidPhoneNumberMsg = t("validations.invalidPhoneNumber");
  const branchOffice = 2;
  const oibFormat = t("validations.oibFormat");
  const oibFormatValidation = t("validations.oibValidation");
  const dateFromLessThanDateToMsg = t("validations.dateFromLessThanDateTo");
  const requiredAtLeastOneMsg = t("validations.requiredAtLeastOne");
  const requiredAtMaxFiveMsg = t("validations.requiredAtMaxFive");
  const documentTooBig = t("errorCode.Document.TooBig");
  const documentWrongFormat = t("errorCode.Document.WrongFormat");
  const requiredOibCheck = t("validations.requiredOibCheck");

  return yup.object().shape({
    email: yup.string().nullable().trim().required(requiredMsg)
      .matches(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        invalidEmailMsg),
    phoneNumber: yup.string().nullable().trim().required(requiredMsg)
      .matches(/\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}/,
        invalidPhoneNumberMsg),
    documentFoundingAct: yup.mixed().nullable().required(requiredMsg).test('documentFoundingAct', documentTooBig, val => {
      if (val && val.id !== -1) {
        return true;
      }
      if (val && val.file.size <= 10485760) {
        return true;
      }
      return false;
    }).test('documentFoundingAct', documentWrongFormat, val => {
      if (val) {
        var split = val.fileName.split('.');
        var ext = split[split.length - 1];

        if (ext === "pdf" || ext === "jpg" || ext === "jpeg" || ext === "png" || ext === "gif" || ext === "tiff" || ext === "bmp") {
          return true;
        }
      }

      return false;
    }),
    documentUsageAgreement: yup.mixed().nullable().required(requiredMsg).test('documentUsageAgreement', documentTooBig, val => {
      if (val && val.id !== -1) {
        return true;
      }
      if (val && val.file.size <= 10485760) {
        return true;
      }
      return false;
    }).test('documentUsageAgreement', documentWrongFormat, val => {
      if (val) {
        var split = val.fileName.split('.');
        var ext = split[split.length - 1];

        if (ext === "pdf" || ext === "jpg" || ext === "jpeg" || ext === "png" || ext === "gif" || ext === "tiff" || ext === "bmp") {
          return true;
        }
      }

      return false;
    }),
    documentMinimumRequirement: yup.mixed().nullable().required(requiredMsg).test('documentMinimumRequirement', documentTooBig, val => {
      if (val && val.id !== -1) {
        return true;
      }
      if (val && val.file.size <= 10485760) {
        return true;
      }
      return false;
    }).test('documentMinimumRequirement', documentWrongFormat, val => {
      if (val) {
        var split = val.fileName.split('.');
        var ext = split[split.length - 1];

        if (ext === "pdf" || ext === "jpg" || ext === "jpeg" || ext === "png" || ext === "gif" || ext === "tiff" || ext === "bmp") {
          return true;
        }
      }
      return false;
    }),
    founders: yup.array().of(
      yup.object().nullable().shape({
        commercialCourt: yup.number().required(requiredMsg)
      })
    ),
    locations: yup.array().of(
      yup.object().shape({
        address: yup.string().when("deleteState", {
          is: LocationDeleteState.None,
          then: yup.string().required(requiredMsg).max(255, t("validations.maximalStringLength", { maxLength: 255 }))
        }),
        town: yup.object().when("deleteState", (deleteState, schema) => {
          if (deleteState === LocationDeleteState.None) {
            return yup.object().shape({ id: yup.number().moreThan(0, requiredMsg).required(requiredMsg) })
          }
          return schema;
        }),
        locationUsageAgreement: yup.mixed().nullable(true).when("deleteState", (deleteState, schema) => {
          if (deleteState === LocationDeleteState.None) {
            return yup.mixed().nullable().required(requiredMsg).test('locationUsageAgreement', documentTooBig, val => {
              if (val && val.id !== -1) {
                return true;
              }
              if (val && val.file.size <= 10485760) {
                return true;
              }
              return false;
            }).test('locationUsageAgreement', documentWrongFormat, val => {
              if (val) {
                var split = val.fileName.split('.');
                var ext = split[split.length - 1];

                if (ext === "pdf" || ext === "jpg" || ext === "jpeg" || ext === "png" || ext === "gif" || ext === "tiff" || ext === "bmp") {
                  return true;
                }
              }
              return false;
            });
          }
          return schema;
        }),
        locationMinimumRequirementSolution: yup.mixed().nullable(true)
          .when("deleteState", {
            is: LocationDeleteState.None,
            then: yup.mixed().nullable(true).when("locationTypeId", (locationTypeId, schema) => {
              return yup.mixed().nullable(true).required(requiredMsg).test('locationMinimumRequirementSolution', documentTooBig, val => {
                if (val && val.id !== -1) {
                  return true;
                }
                if (val && val.file.size <= 10485760) {
                  return true;
                }
                return false;
              }).test('locationMinimumRequirementSolution', documentWrongFormat, val => {
                if (val) {
                  var split = val.fileName.split('.');
                  var ext = split[split.length - 1];

                  if (ext === "pdf" || ext === "jpg" || ext === "jpeg" || ext === "png" || ext === "gif" || ext === "tiff" || ext === "bmp") {
                    return true;
                  }
                }
                return false;
              });

              return schema;
            }),
            otherwise: yup.object().nullable(true)
          })
      })
    ),
    andragogicLeaders: yup.array().of(
      yup.object().shape({
        oib: yup
          .string()
          .when("deleteState", {
            is: AndragogicLeaderDeleteState.None,
            then: yup
              .string().trim()
              .matches(/^[0-9]+$/, oibFormat)
              .required(requiredMsg)
              .min(11, oibFormat)
              .max(11, oibFormat)
              .test("checkOibValidation", "isRequired", function (value) {
                var length = value?.length;
                var oibValue = value ?? "";

                if (length) {
                  var residue = 10;
                  for (var i = 0; i < length - 1; i++) {
                    var digit = parseInt(oibValue[i]);
                    var midResult = (digit + residue) % 10;
                    midResult = midResult === 0 ? 10 : midResult;
                    residue = (midResult * 2) % 11;
                  }

                  var lastDigit = parseInt(oibValue[length - 1]);
                  var controllCharacter = 11 - residue;
                  if (controllCharacter === lastDigit) {
                    return true;
                  } else if (controllCharacter === 10 && 0 === lastDigit) {
                    return true;
                  } else {
                    return this.createError({
                      message: oibFormatValidation,
                    });
                  }
                } else {
                  return this.createError({
                    message: oibFormatValidation,
                  });
                }
              })
          }),
        firstName: yup.object().nullable(true).when("deleteState", (deleteState, schema) => {
          if (deleteState === AndragogicLeaderDeleteState.None) {
            return yup.string()
              .trim()
              .required(requiredMsg)
              .max(128, t("validations.tooLong", {
                numberOfCharacters: 128,
              }))
          }
          return schema;
        }),
        lastName: yup.object().nullable(true).when("deleteState", (deleteState, schema) => {
          if (deleteState === AndragogicLeaderDeleteState.None) {
            return yup.string()
              .trim()
              .required(requiredMsg)
              .max(128, t("validations.tooLong", {
                numberOfCharacters: 128,
              }))
          }
          return schema;
        }),
        dateFrom: yup.object().nullable(true).when("deleteState", (deleteState, schema) => {
          if (deleteState === AndragogicLeaderDeleteState.None) {
            return yup
              .date()
              .required(requiredMsg)
              .nullable()
              .when("dateTo", (dateTo, schema) => {
                if (dateTo) {
                  return yup.date().required(requiredMsg).nullable().max(dateTo, dateFromLessThanDateToMsg)
                } else {
                  return schema;
                }
              })
          }
          return schema;
        }),
        locations: yup.object().nullable(true).when("deleteState", (deleteState, schema) => {
          if (deleteState === AndragogicLeaderDeleteState.None) {
            return yup.array()
              .min(1, requiredAtLeastOneMsg)
              .max(5, requiredAtMaxFiveMsg)
          }
          return schema;
        }),
        copyOdDiploma: yup.mixed().nullable(true).when("deleteState", (deleteState, schema) => {
          if (deleteState === LocationDeleteState.None) {
            return yup.mixed().nullable().required(requiredMsg).test('copyOdDiploma', documentTooBig, val => {

              if (val === null || (val && val.id !== -1)) {
                return true;
              }
              if (val && val.file.size <= 10485760) {
                return true;
              }
              return false;
            }).test('copyOdDiploma', documentWrongFormat, val => {
              if (val === null || (val && val.id !== -1)) {
                return true;
              }
              if (val) {
                var split = val.fileName.split('.');
                var ext = split[split.length - 1];

                if (ext === "pdf" || ext === "jpg" || ext === "jpeg" || ext === "png" || ext === "gif" || ext === "tiff" || ext === "bmp") {
                  return true;
                }
              }
              return false;
            });
          }
          return schema;
        }),
        additionalDocument: yup.mixed().nullable(true).when("deleteState", (deleteState, schema) => {
          if (deleteState === LocationDeleteState.None) {
            return yup.mixed().nullable(true).test('additionalDocument', documentTooBig, val => {
              if (!val || (val && val.id !== -1)) {
                return true;
              }
              if (val && val.file.size <= 10485760) {
                return true;
              }
              return false;
            }).test('additionalDocument', documentWrongFormat, val => {
              if (!val || (val && val.id !== -1)) {
                return true;
              }
              if (val) {
                var split = val.fileName.split('.');
                var ext = split[split.length - 1];

                if (ext === "pdf" || ext === "jpg" || ext === "jpeg" || ext === "png" || ext === "gif" || ext === "tiff" || ext === "bmp") {
                  return true;
                }
              }
              return false;
            });
          }
          return schema;
        }),
        oibChecked: yup.boolean().test('oibChecked', requiredOibCheck, val => {
          if (val === false) {
            return false;
          }
          return true;
        })
      }))
  });
};